const hiddenClass = 'selling_plan_theme_integration--hidden';

class SellingPlansWidget {
  constructor(sellingPlansWidgetContainer) {
    this.enablePerformanceObserver();
    this.sellingPlansWidgetContainer = sellingPlansWidgetContainer;
    this.appendSellingPlanInputs();
    this.updateSellingPlanInputsValues();
    this.listenToVariantChange();
    this.listenToSellingPlanFormRadioButtonChange();
    this.listenToSubscribeCheckbox();
    this.updatePrice();
    this.handleVariantChange();
  }

  get sectionId() {
    return this.sellingPlansWidgetContainer.getAttribute('data-section-id');
  }

  get shopifySection() {
    return document.querySelector(`#shopify-section-${this.sectionId}`);
  }

  /*
    We are careful to target the correct form, as there are instances when we encounter an installment form that we specifically aim to avoid interacting with.
  */
  get variantIdInput() {
    const form = this.addToCartForms[0];
    if (!form) return null;
    return form.querySelector('input[name="id"], input[name="variant_id"]');
  }

  get visibleSellingPlanForm() {
    if (!this.variantIdInput?.value) return null;
    return this.shopifySection.querySelector(
      `section[data-variant-id="${this.variantIdInput.value}"]`,
    );
  }

  get sellingPlanInput() {
    return this.shopifySection.querySelector('.selected-selling-plan-id');
  }

  get addToCartForms() {
    return this.shopifySection.querySelectorAll('[action*="/cart/add"]');
  }

  /*
    To enable the addition of a selling plan to a cart, it's necessary to include an input with the name "selling_plan", which will carry the selling ID as its value. When a buyer clicks on 'add to cart', the appropriate selling plan ID is added to their cart.
  */
  appendSellingPlanInputs() {
    if (!this.sellingPlanInput) return;
    this.addToCartForms.forEach((addToCartForm) => {
      if (addToCartForm.querySelector('input[name="selling_plan"]')) return;
      addToCartForm.appendChild(this.sellingPlanInput.cloneNode());
    });
  }

  showSellingPlanForm(sellingPlanFormForSelectedVariant) {
    sellingPlanFormForSelectedVariant?.classList?.remove(hiddenClass);
  }

  hideSellingPlanForms(sellingPlanFormsForUnselectedVariants) {
    sellingPlanFormsForUnselectedVariants.forEach((element) => {
      element.classList.add(hiddenClass);
    });
  }

  /*
    Each product variant comes with a selling plan selection box that the buyer can interact with.
    When a buyer chooses a different variant, we ensure that only the relevant selling plan selection box is displayed.
    This guarantees that only the selling plan associated with the selected variant is shown.
  */
 handleSellingPlanFormVisibility() {
    if (!this.variantIdInput?.value) return;
    const sellingPlanFormForSelectedVariant = this.shopifySection.querySelector(
      `section[data-variant-id="${this.variantIdInput.value}"]`,
    );
    const sellingPlanFormsForUnselectedVariants =
      this.shopifySection.querySelectorAll(
        `.selling_plan_theme_integration:not([data-variant-id="${this.variantIdInput.value}"])`,
      );
    this.showSellingPlanForm(sellingPlanFormForSelectedVariant);
    this.hideSellingPlanForms(sellingPlanFormsForUnselectedVariants);
  }

  handleVariantChange() {
    this.handleSellingPlanFormVisibility();
    this.updateSellingPlanInputsValues();
    this.listenToSellingPlanFormRadioButtonChange();
    this.listenToSubscribeCheckbox();
    this.updatePrice();//added by me
  }

  /*
    The functions listenToVariantChange() and listenToAddToCartForms() are implemented to track when a product variant is altered or when the product form is updated.
    The identification of the variant is crucial as it dictates which selling plan box should be displayed.
  */
  listenToVariantChange() {
    this.listenToAddToCartForms();
    if (!this.variantIdInput || this.variantIdInput.tagName !== 'INPUT') return;

    this.variantIdInput.addEventListener('change', () => this.handleVariantChange());

    document.addEventListener(window.MainEvents?.variantUpdate || 'variant:update', () => {
      window.requestAnimationFrame(() => this.updatePrice());
    });
  }

  listenToAddToCartForms() {
    this.addToCartForms.forEach((addToCartForm) => {
      addToCartForm.addEventListener('change', () => {
        this.handleVariantChange();
      });
    });
  }

  get priceContainer() {
    return this.shopifySection.querySelector(
      'product-price .price-product-container, .price-product-container',
    );
  }

  get oneTimeVariantPrice() {
    const oneTimeRadio = this.visibleSellingPlanForm?.querySelector(
      'input[data-radio-type="one_time_purchase"]',
    );
    return oneTimeRadio?.dataset.variantPrice;
  }

  get subscriptionSalePrice() {
    return this.selectedPurchaseOption?.dataset.variantPrice;
  }

  get salePriceColor() {
    const container = this.priceContainer;
    const existingSaleEl = container?.querySelector('.price-item--sale');
    return (
      existingSaleEl?.style.getPropertyValue('--sale-price-color')?.trim() ||
      getComputedStyle(document.documentElement).getPropertyValue('--sale-price-color')?.trim() ||
      '#EF3626'
    );
  }

  get preservedPriceExtras() {
    const container = this.priceContainer;
    if (!container) return '';

    const unitPriceEl = container.querySelector('.unit-price, small.unit-price');
    return unitPriceEl ? unitPriceEl.outerHTML : '';
  }

  removeLegacySubscriptionElements() {
    this.shopifySection.querySelectorAll('.subscription-price').forEach((el) => el.remove());
  }

  applySubscriptionPriceDisplay() {
    const container = this.priceContainer;
    if (!container) return;

    this.removeLegacySubscriptionElements();

    const oneTimePrice = this.oneTimeVariantPrice;
    const subscriptionPrice = this.subscriptionSalePrice;
    if (!oneTimePrice || !subscriptionPrice) return;

    const unitPriceHtml = this.preservedPriceExtras;
    const saleColor = this.salePriceColor;

    container.classList.add('price--on-sale');
    container.dataset.subscriptionPriceActive = 'true';

    container.innerHTML = `
      <span role="group">
        <span class="visually-hidden">Sale price</span>
        <span class="price price-item price-item--sale price-item--last subscription-sale-price" style="--sale-price-color: ${saleColor};">${subscriptionPrice}</span>
      </span>
      <span role="group">
        <span class="visually-hidden">Regular price</span>
        <span class="compare-at-price price-item price-item--regular subscription-compare-price">${oneTimePrice}</span>
      </span>
      ${unitPriceHtml}
    `;
  }

  restoreOneTimePriceDisplay() {
    const container = this.priceContainer;
    if (!container?.dataset.subscriptionPriceActive) return;

    const oneTimeRadio = this.visibleSellingPlanForm?.querySelector(
      'input[data-radio-type="one_time_purchase"]',
    );
    const price = oneTimeRadio?.dataset.variantPrice;
    const compareAt = oneTimeRadio?.dataset.variantCompareAtPrice;
    const unitPriceHtml = this.preservedPriceExtras;
    const hasCompare = compareAt && compareAt !== price;

    delete container.dataset.subscriptionPriceActive;

    if (hasCompare) {
      const saleColor = this.salePriceColor;
      container.classList.add('price--on-sale');
      container.innerHTML = `
        <span role="group">
          <span class="visually-hidden">Sale price</span>
          <span class="price price-item price-item--sale price-item--last" style="--sale-price-color: ${saleColor};">${price}</span>
        </span>
        <span role="group">
          <span class="visually-hidden">Regular price</span>
          <span class="compare-at-price price-item price-item--regular">${compareAt}</span>
        </span>
        ${unitPriceHtml}
      `;
    } else {
      container.classList.remove('price--on-sale');
      container.innerHTML = `
        <span class="price price-item price-item--sale price-item--last">${price}</span>
        ${unitPriceHtml}
      `;
    }
  }

  get regularPriceElement() {
    return this.priceContainer?.querySelector('.subscription-compare-price, .compare-at-price');
  }

  get salePriceElement() {
    return this.priceContainer?.querySelector('.subscription-sale-price, .price-item--sale.price-item--last');
  }

  get regularPriceValue() {
    return this.regularPriceElement ? this.regularPriceElement.textContent.trim() : null;
  }

  get regularUnitPriceElement() {
    return this.shopifySection.querySelector('.single-price-container');
  }

  get saleUnitPriceElement() {
    return this.shopifySection.querySelector('.single-sale-price-container');
  }

  //   get salePriceValue() {
  //     let subscriptionPriceValue = this.shopifySection.querySelector('.subscription-price .money'); // Original price string
  // console.log('✌️subscriptionPriceValue --->', subscriptionPriceValue);

  //     return subscriptionPriceValue[1] ? subscriptionPriceValue[1].textContent.trim() : null;
  //   }

  get subscriptionUnitPriceValue() {
    return this.regularUnitPriceElement ? this.regularUnitPriceElement.textContent.trim() : null;
  }

  get sellingPlanAllocationPrice() {
    return document.getElementById(
      `${this.selectedPurchaseOption.dataset.sellingPlanGroupId}_allocation_price`,
    );
  }


  get selectedPurchaseOptionPrice() {
    return this.selectedPurchaseOption.dataset.variantPrice;
  }

  get selectedPurchaseOptionComparedAtPrice() {
    return this.selectedPurchaseOption.dataset.variantCompareAtPrice;
  }

  get price() {
    return this.sellingPlanAllocationPrices.price ?? null;
  }

  /*
    We aim to ascertain whether a compared price exists, which would indicate that the currently selected input has a discount applied to it.
    If a discount is detected, the discounted price is displayed; otherwise, the regular price is shown.
  */
  updatePrice() {
    if (this.sellingPlanInputValue) {
      this.applySubscriptionPriceDisplay();
      this.showSaleUnitPrice();
      this.hideRegularUnitPrice();
    } else {
      this.restoreOneTimePriceDisplay();
      this.hideSaleUnitPrice();
      this.showRegularUnitPrice();
    }
  }

  hideSalePrice() {
    this.restoreOneTimePriceDisplay();
  }

  hideRegularPrice() {
    // Compare-at price is shown intentionally during subscription pricing.
  }

  showRegularPrice() {
    this.restoreOneTimePriceDisplay();
  }

  showSalePrice() {
    this.applySubscriptionPriceDisplay();
  }




  get regularUnitPriceElement() {
    return this.shopifySection.querySelector('.single-price-container');
  }

  get saleUnitPriceElement() {
    return this.shopifySection.querySelector('.single-sale-price-container');
  }


  showSaleUnitPrice() {
    if (this.regularUnitPriceElement) {
      let existingSalePrice = this.regularUnitPriceElement.parentNode.querySelector(".single-sale-price-container");

      let subscriptionUnitPriceElement;

      if (!existingSalePrice) {
        subscriptionUnitPriceElement = this.regularUnitPriceElement.parentNode.cloneNode(true);
      } else {
        subscriptionUnitPriceElement = existingSalePrice;
        this.saleUnitPriceElement.style.display = 'block';
      }

      let subscriptionUnitPriceValue = this.subscriptionUnitPriceValue; // Original price string
      let variantUnits = this.visibleSellingPlanForm.dataset.variantUnits;
      let variantPrice = this.selectedPurchaseOptionPrice;
      let variantPriceRemoved = variantPrice.match(/\d+(\.\d+)?/)[0];

      let newUnitPrice = parseFloat(variantPriceRemoved / variantUnits).toFixed(2);

      const result = subscriptionUnitPriceValue.replace(/(\W{1,2}\s?)\d+(\.\d+)?(\/\w+)/, (_, p1, p2, p3) => `${p1}${newUnitPrice}${p3}`);


      subscriptionUnitPriceElement.textContent = result; // Set new value (replace old)
      subscriptionUnitPriceElement.classList.add("single-sale-price-container");

      this.regularUnitPriceElement.parentNode.appendChild(subscriptionUnitPriceElement);
    }
  }


  hideSaleUnitPrice() {
    if (this.saleUnitPriceElement) {
      this.saleUnitPriceElement.style.display = 'none';
    }
  }

  hideRegularUnitPrice() {
    if (this.regularUnitPriceElement) {
      this.regularUnitPriceElement.style.display = 'none';
    }
  }

  showRegularUnitPrice() {
    if (this.regularUnitPriceElement) {
      this.regularUnitPriceElement.style.display = 'inline-block';
    }
  }




  get sellingPlanInputs() {
    return this.shopifySection.querySelectorAll('.selected-selling-plan-id');
  }

  updateSellingPlanInputsValues() {
    const value = this.sellingPlanInputValue || '';
    this.sellingPlanInputs.forEach((sellingPlanInput) => {
      sellingPlanInput.value = value;
    });
  }

  get sellingPlanInputValue() {
    return this.selectedPurchaseOption?.dataset.sellingPlanId ?? null;
  }

  get subscribeCheckbox() {
    const variantId = this.variantIdInput?.value;
    if (!variantId) return null;
    return document.getElementById(`subscribe_checkbox_${variantId}`);
  }

  get selectedPurchaseOption() {
    if (!this.visibleSellingPlanForm) return null;

    const checkbox = this.subscribeCheckbox;
    if (checkbox && !checkbox.checked) {
      return this.visibleSellingPlanForm.querySelector(
        'input[data-radio-type="one_time_purchase"]',
      );
    }

    return this.visibleSellingPlanForm.querySelector(
      'input[type="radio"][data-selling-plan-id]:checked',
    );
  }

  set selectedPurchaseOption(selectedPurchaseOption) {
    this._selectedPurchaseOption = selectedPurchaseOption;
  }

  handleRadioButtonChange(selectedPurchaseOption) {
    this.selectedPurchaseOption = selectedPurchaseOption;
    this.updateSellingPlanInputsValues();
    this.updatePrice();
  }

  listenToSellingPlanFormRadioButtonChange() {
    this.visibleSellingPlanForm
      ?.querySelectorAll('input[type="radio"]')
      .forEach((radio) => {
        if (radio.dataset.sellingPlanListenerAttached) return;
        radio.dataset.sellingPlanListenerAttached = 'true';
        radio.addEventListener('change', (event) => {
          this.handleRadioButtonChange(event.target);
        });
      });
  }

  listenToSubscribeCheckbox() {
    const checkbox = this.subscribeCheckbox;
    if (!checkbox || checkbox.dataset.sellingPlanListenerAttached) return;
    checkbox.dataset.sellingPlanListenerAttached = 'true';
    checkbox.addEventListener('change', () => {
      this.updateSellingPlanInputsValues();
      this.updatePrice();
    });
  }

  enablePerformanceObserver() {
    const performanceObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.initiatorType !== 'fetch') return;

        const url = new URL(entry.name);
        /*
          When a buyer selects a product variant, a fetch request is initiated.
          Upon completion of this fetch request, we update the price to reflect the correct value.
        */
        if (url.search.includes('variant') || url.search.includes('variants')) {

          // this.updatePrice();
        }
      });
    });

    performanceObserver.observe({ entryTypes: ['resource'] });
  }
}



document.addEventListener("DOMContentLoaded", function () {
  const container = document.querySelector(".selling_plan_app_container");
  if (!container) return; // Exit if container is not found

  // Create a single tooltip and append it to the container
  const tooltip = document.createElement("div");
  tooltip.className = "tooltip-box";
  tooltip.style.opacity = "0"; // Initially hidden
  tooltip.style.transform = "translateY(-5px)";
  tooltip.style.transition = "opacity 0.3s ease, transform 0.3s ease"; // Smooth transition
  tooltip.style.visibility = "hidden"; // Prevent CLS
  tooltip.style.pointerEvents = "none"; // Avoid accidental interactions
  tooltip.style.position = "absolute"; // Ensure correct placement
  tooltip.style.maxWidth = "600px"; // Match CSS
  tooltip.style.wordWrap = "break-word"; // Prevent overflow
  tooltip.style.width = container.offsetWidth + "px"; // ✅ Match container width

  tooltip.innerHTML = `Have complete control of your subscriptions<br><br>
    Skip, reschedule, edit, or cancel deliveries anytime, based on your needs. 
    <a href='/pages/subscriptions' style='color: #007bff; text-decoration: underline;'>More info</a>`;

  container.appendChild(tooltip); // Append tooltip inside container

  let activeIcon = null; // Track which icon was clicked

  document.querySelectorAll(".tooltip-icon").forEach(icon => {
    icon.addEventListener("click", function (event) {
      event.stopPropagation(); // Prevent event from bubbling to document

      if (tooltip.style.opacity === "1" && activeIcon === icon) {
        hideTooltip();
      } else {
        activeIcon = icon;
        showTooltip(icon);
      }
    });
  });

  function showTooltip(icon) {
    tooltip.style.visibility = "visible";
    tooltip.style.opacity = "1";
    tooltip.style.transform = "translateY(0)";
    
    // ✅ Update width dynamically in case of layout changes
    tooltip.style.width = container.offsetWidth + "px";

    // Position tooltip below the clicked icon
    const iconRect = icon.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    let leftPosition = window.innerWidth <= 768 ? 10 : 15;
    let topPosition = icon.offsetTop + icon.offsetHeight + 5; // Slight gap below icon

    tooltip.style.left = `${leftPosition}px`; // Set left position
    tooltip.style.top = `${topPosition}px`;

    tooltip.style.pointerEvents = "auto"; // Allow interaction when open
  }

  function hideTooltip() {
    tooltip.style.opacity = "0";
    tooltip.style.transform = "translateY(-5px)";
    tooltip.style.pointerEvents = "none"; // Disable interaction
    setTimeout(() => {
      tooltip.style.visibility = "hidden";
      activeIcon = null;
    }, 300);
  }

  // ✅ Click anywhere outside (except tooltip-icon) to hide
  document.addEventListener("click", function (event) {
    if (!event.target.classList.contains("tooltip-icon") && !tooltip.contains(event.target)) {
      hideTooltip();
    }
  });
});














document.addEventListener("DOMContentLoaded", function () { 
  const syncSellingPlanWidgets = () => {
    document.querySelectorAll('.selling_plan_app_container').forEach((container) => {
      const widget = container._sellingPlansWidget;
      if (widget) {
        widget.updateSellingPlanInputsValues();
        widget.updatePrice();
      }
    });
  };

  // Helper function to handle the subscription visibility
  const toggleSubscriptionOptions = (checkbox, variantId) => {
    const subscriptionOptions = document.getElementById('subscription_options_' + variantId);
    const oneTimePurchase = document.getElementById('one_time_purchase_' + variantId);

    if (checkbox.checked) {
      subscriptionOptions.classList.remove('selling_plan_theme_integration--hidden');
      oneTimePurchase.classList.add('selling_plan_theme_integration--hidden');

      // Ensure the first subscription plan is selected by default if nothing is stored
      const savedSellingPlanId = localStorage.getItem('selected_selling_plan_' + variantId);
      let radioButton;
      
      if (savedSellingPlanId) {
        radioButton = subscriptionOptions.querySelector(`input[data-selling-plan-id="${savedSellingPlanId}"]`);
      }
      
      if (!radioButton) {
        radioButton = subscriptionOptions.querySelector('input[data-selling-plan-id]');
      }

      if (radioButton) {
        radioButton.checked = true;
        localStorage.setItem('selected_selling_plan_' + variantId, radioButton.getAttribute('data-selling-plan-id'));
        radioButton.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else {
      subscriptionOptions.classList.add('selling_plan_theme_integration--hidden');
      oneTimePurchase.classList.remove('selling_plan_theme_integration--hidden');

      // Select one-time purchase option
      const oneTimeRadioButton = oneTimePurchase.querySelector('input[type="radio"]');
      if (oneTimeRadioButton) {
        oneTimeRadioButton.checked = true;
        oneTimeRadioButton.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    syncSellingPlanWidgets();
  };

  // Loop over each checkbox
  document.querySelectorAll('[id^="subscribe_checkbox_"]').forEach(function (checkbox) {
    const variantId = checkbox.getAttribute('data-variant-id');
    const savedState = localStorage.getItem(checkbox.id);

    // Initialize checkbox based on localStorage state
    checkbox.checked = savedState === 'true';

    // Handle subscription options visibility and radio button selection
    toggleSubscriptionOptions(checkbox, variantId);

    // Handle checkbox state changes
    checkbox.addEventListener('change', function () {
      // Store checkbox state in localStorage
      localStorage.setItem(checkbox.id, checkbox.checked);

      // Toggle subscription options visibility based on checkbox state
      toggleSubscriptionOptions(checkbox, variantId);
    });

    // Attach event listeners for radio buttons within subscription options
    const subscriptionOptions = document.getElementById('subscription_options_' + variantId);
    if (subscriptionOptions) {
      subscriptionOptions.querySelectorAll('input[type="radio"]').forEach(function (radioButton) {
        radioButton.addEventListener('change', function () {
          // Store the selected selling plan id in localStorage
          const sellingPlanId = radioButton.getAttribute('data-selling-plan-id');
          localStorage.setItem('selected_selling_plan_' + variantId, sellingPlanId);
          syncSellingPlanWidgets();
        });
      });
    }
  });

  // Initialize SellingPlansWidget for each widget container
  document.querySelectorAll('.selling_plan_app_container').forEach((sellingPlansWidgetContainer) => {
    sellingPlansWidgetContainer._sellingPlansWidget = new SellingPlansWidget(sellingPlansWidgetContainer);
  });
});














document.addEventListener("variantSelected", () => {
  document
    .querySelectorAll('.selling_plan_app_container')
    .forEach((sellingPlansWidgetContainer) => {
      if (!sellingPlansWidgetContainer._sellingPlansWidget) {
        sellingPlansWidgetContainer._sellingPlansWidget = new SellingPlansWidget(sellingPlansWidgetContainer);
      } else {
        sellingPlansWidgetContainer._sellingPlansWidget.handleVariantChange();
      }
    });
});